import { useEffect, useState } from 'react';
import { fetchSalesOrders } from './salesOrders';
import Layout from '../../components/layouts/Main';
import { Row, Col, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PulseLoader } from "react-spinners";
import SalesOrderTable from "../../components/salesOrders/SalesOrderTable";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import ReportDownloader from "../../components/common/ReportDownloader";

function SalesOrderList() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Permission checks
  const canViewSO = useHasPermission(PERMISSIONS.SO_VIEW);
  const canCreateSO = useHasPermission(PERMISSIONS.SO_CREATE);
  const canCreateInHouseSO = useHasPermission(PERMISSIONS.SO_INHOUSE_CREATE);
  const canEditSO = useHasPermission(PERMISSIONS.SO_EDIT);
  const canDeleteSO = useHasPermission(PERMISSIONS.SO_DELETE);

  useEffect(() => { 
    async function loadOrders() {
      if (!canViewSO) {
        toast.error("You don't have permission to view sales orders");
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetchSalesOrders('?populate=true&populateItems=true');
        setOrders(res.data);
      } catch (err) {
        console.error("Failed to fetch sales orders", err);
        toast.error("Failed to load sales orders");
      } finally {
        setIsLoading(false);
      }
    }

    loadOrders();
  }, [canViewSO]);

  // If user can't view SO, show permission denied
  if (!canViewSO) {
    return (
      <Layout title="Sales Orders">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
            <li className="breadcrumb-item active">Sales Orders</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view sales orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout title="Sales Orders">

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Sales Orders</li>
        </ol>
      </nav>

      {/* Sales Orders Header */}
      <Row className="mb-4 gy-3">
        <Col className="col">
          <h2>Sales Orders</h2>
          <small className="text-muted">
            {canEditSO ? "View and manage" : "View"} sales orders
            {!canCreateSO && !canEditSO && !canDeleteSO && " (read-only)"}
          </small>
        </Col>
        <Col className="text-end col-auto">
          <div className="d-flex justify-content-end flex-wrap gap-2">
            {/* <PermissionGate
              require={PERMISSIONS.SO_CREATE}
              fallback={
                <Button variant="outline-primary" disabled title="You don't have permission to create sales orders">
                  <i className="fas fa-plus me-2" /> Create Sales Order
                </Button>
              }
            > */}
            {canCreateSO && (
              <>
                {canCreateInHouseSO && (
                  <Link to="/sales-orders/create-in-house" className="btn btn-outline-success">
                    <i className="fas fa-building me-2" /> Create In-House SO
                  </Link>
                )}
                <Link to="/sales-orders/create" className="btn btn-primary">
                  <i className="fas fa-plus me-2" /> Create Sales Order
                </Link>
              </>
            )}
            {/* <Button variant="outline-primary" onClick={() => setShowExportModal(true)}>
              <FiDownload className="me-2" /> Download Report
            </Button> */}
            {/* </PermissionGate> */}
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <PulseLoader
          color="#014b7cbb"
          size={9}
        />
      ) : (
        <>
          <SalesOrderTable 
            orders={orders} 
            canEdit={canEditSO}
            canDelete={canDeleteSO}
            canView={canViewSO}
          />

          <ReportDownloader 
            show={showExportModal}
            handleClose={() => setShowExportModal(false)}
            data={orders}
            availableColumns={[
              { header: 'SN', key: 'sn' },
              { header: 'Order Number', key: 'orderNumber' },
              { header: 'Order Date', key: 'orderDate' },
              { header: 'Buyer Name', key: 'buyer.business_name' },
              { header: 'Broker Name', key: 'broker.business_name' },
              { header: 'EDD', key: 'EDD' },
              { header: 'Product Name', key: 'items.product.productName' },
              { header: 'Order Quantity', key: 'items.quantity' },
              { header: 'Weight Type', key: 'items.grossOrNet' },
              { header: 'Custom Marking', key: 'items.customMarking' },
              { header: 'Packing Material', key: 'items.packingBagType.name' },
              { header: 'Unit Price', key: 'items.unitPrice' },
              { header: 'Base Amount', key: 'items.baseAmount' },
              { header: 'Tax', key: 'items.tax' },
              { header: 'Tax Amount', key: 'items.taxAmount' },
              { header: 'Total Item Amount', key: 'items.totalPrice' },
              { header: 'Quality Check', key: 'labTestRequired' },
              { header: 'Status', key: 'status' },
              { header: 'Transportation Amount', key: 'transportationAmount' },
              { header: 'Total SO Amount', key: 'totalAmount' },
              { header: 'Payment Status', key: 'paymentStatus' }
            ]}
            title="Export Sales Orders"
            fileName="sales_orders"
          />
        </>
      )}

    </Layout>
  );
}

export default SalesOrderList;
